<template>
  <s-table
    class="additional-fields-table"
    sticky-header
    :items="items"
    :fields="tableFields"
    :busy="loading"
  >
    <template
      v-for="field in tableFields"
      #[`cell(${field.key})`]="data"
    >
      <span
        :key="field.key"
        class="additional-fields-table__field"
      >
        {{ getFieldData(data) }}
      </span>
    </template>
    <template #cell(openMethodsAndDiagnosis)="data">
      <button
        class="additional-fields-table__diagnosis-window-button"
        @click="goToMethodsAndDiagnostics(data.item)"
      >
        <i class="fas fa-external-link-alt"/>
      </button>
    </template>
    <template #cell(operationEntry)="data">
      <span>
        <span> {{ getDateValue(data) | dateTimeFilter(operationEntryFilterOptions) }} </span>
      </span>
    </template>
    <template #cell(transformerType)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(transformerApplication)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(withTertiaryWinding)="data">
      <span>
        <span> {{ getBooleanValue(data) }} </span>
      </span>
    </template>
    <template #cell(tensionRegulation)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(insulationType)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(oilType)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(communicatingOilOltcTransformer)="data">
      <span>
        <span> {{ getBooleanValue(data) }} </span>
      </span>
    </template>
    <template #cell(circuitBreakerType)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(circuitBreakerInstallationPlace)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(electricArchExtinctionMethod)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(mechanismOperation)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(disconnectorType)="data">
     <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(maneuverType)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(disconnectorInstallationPlace)="data">
      <span>
        <span> {{ getEnumValue(data) }} </span>
      </span>
    </template>
    <template #cell(manufactureYear)="data">
      <span>
        <span> {{ getNumberValue(data) }} </span>
      </span>
    </template>
  </s-table>
</template>

<script>
import allAdditionalFields
  from '@/domain/data/generalPanel/reports/additionalFields/allAdditionalFields';
import methodsDirectDictionary
  from '@/domain/data/generalPanel/reports/diagnosticsHistory/methodsDirectDictionary';
import testsDictionary from '@/domain/data/generalPanel/reports/diagnosticsHistory/testsDictionary';
import { formats, timezones } from '@/constants/localizer';

export default {
  name: 'AdditionalFieldsTable',
  props: {
    items: {
      type: Array,
      default: () => ([]),
    },
    fields: {
      type: Array,
      default: () => ([]),
    },
    additionalFieldsToShow: {
      type: Array,
      default: () => ([]),
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tableFields() {
      const mappedAdditionalFields = allAdditionalFields(this).map(item => ({
        ...item,
        key: item.value,
        label: item.text,
      }));
      const additionalFieldsToShow = mappedAdditionalFields.filter(
        field => this.additionalFieldsToShow.includes(field.key),
      );
      return this.fields.concat(additionalFieldsToShow);
    },
    operationEntryFilterOptions() {
      return {
        format: formats.DATE_SHORT,
        timeZone: timezones.UTC,
      };
    },
  },
  methods: {
    goToMethodsAndDiagnostics(item) {
      const {
        assetId,
        installationId,
        assetModuleId,
        sampleId: sampleIds,
      } = item;
      const routeData = this.$router.resolve({
        name: 'clean-view.chromatography.methods-and-diagnostics',
        params: {
          assetId,
          installationId,
          assetModuleId,
        },
        query: {
          sampleIds,
        },
      });
      window.open(routeData.href, '_blank');
    },
    getFieldData(data = {}) {
      const { value, field = {} } = data;
      if (typeof value === 'number') {
        return this.formatNumberValue(value, field.precision);
      }
      if (typeof value === 'boolean') {
        return this.getBooleanValue({ value });
      }
      if (field.enumLabelPrefix) {
        return this.getEnumValue(data);
      }
      if (field.isMethod) {
        return this.$t(methodsDirectDictionary[value]);
      }
      if (field.isTest) {
        return testsDictionary(this)[value];
      }
      return value || '-';
    },
    getEnumValue(data = {}) {
      const { field = {}, value } = data;
      return value ? this.$t(`${field.enumLabelPrefix}${value}`) : '-';
    },
    getBooleanValue(data = {}) {
      const { value } = data;
      const values = {
        true: this.$t('general.yes'),
        false: this.$t('general.no'),
      };
      return values[value] || '-';
    },
    getDateValue(data = {}) {
      const { value } = data;
      return value;
    },
    getNumberValue(data = {}) {
      const { value } = data;
      return typeof value === 'number' && !Number.isNaN(value) ? value : '-';
    },
    formatNumberValue(value, decimal) {
      if (!decimal) {
        return this.$n(value);
      }
      return this.$n(value, {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.additional-fields-table
  position: relative
  &__field
    word-break: break-word
  &__diagnosis-window-button
    border: none
    background-color: transparent
    outline: none

    i
      color: var(--color-secondary)

  ::v-deep
    .empty-list
      position: absolute
      width: 90vw
      height: 80%
      display: flex
      flex-direction: column
      justify-content: center
      align-items: center
      padding: 0 !important

    thead tr th
      background-color: var(--color-table-header-bg) !important
</style>
